/**
 * rows: [{ date, table, period, type, side, vendor, total }]
 * lookup[type][side][vendor][date] = total
 *
 * Date separation:
 *   allDates    = all dates returned by backend (display + median reference window)
 *   refDates    = only the median reference dates (before displayFrom)
 *   displayDates = only the dates the user wants to see (>= displayFrom)
 *
 * The median is computed using refDates only — no circular reference.
 * cellStatus and vendorSideDetail evaluate a specific display date
 * against a median built from refDates.
 */

export function buildLookup(rows) {
	const lookup = {};
	rows.forEach(r => {
		if (!lookup[r.type])                   lookup[r.type] = {};
		if (!lookup[r.type][r.side])           lookup[r.type][r.side] = {};
		if (!lookup[r.type][r.side][r.vendor]) lookup[r.type][r.side][r.vendor] = {};
		lookup[r.type][r.side][r.vendor][r.date] = r.total;
	});
	return lookup;
}

export function getUniqueDates(rows) {
	return [...new Set(rows.map(r => r.date))].sort();
}

export function getDisplayDates(rows, displayFrom, displayTo) {
	const all = getUniqueDates(rows);
	if (!displayFrom && !displayTo) return all;
	return all.filter(d =>
		(!displayFrom || d >= displayFrom) &&
    (!displayTo   || d <= displayTo)
	);
}

export function getReferenceDates(rows, displayFrom) {
	const all = getUniqueDates(rows);
	if (!displayFrom) return all;
	// Only dates strictly before the display window
	return all.filter(d => d < displayFrom);
}

export function getUniqueTypes(rows) {
	return [...new Set(rows.map(r => r.type))].sort();
}

export function getUniqueSides(rows) {
	return [...new Set(rows.map(r => r.side))].sort();
}

export function getUniqueVendors(rows) {
	return [...new Set(rows.map(r => r.vendor))].sort();
}

function median(values) {
	if (!values.length) return 0;
	const sorted = [...values].sort((a, b) => a - b);
	const mid    = Math.floor(sorted.length / 2);
	return sorted.length % 2 !== 0
		? sorted[mid]
		: (sorted[mid - 1] + sorted[mid]) / 2;
}

/**
 * Pre-compute vendor medians for a given type+side,
 * using ONLY the reference dates (before the display window).
 */
export function computeVendorMedians(lookup, type, side, refDates) {
	const byVendor = lookup?.[type]?.[side];
	if (!byVendor) return {};
	const result = {};
	Object.keys(byVendor).forEach(vendor => {
		const vals = refDates
			.map(d => byVendor[vendor]?.[d] ?? 0)
			.filter(v => v > 0);
		result[vendor] = median(vals);
	});
	return result;
}

/**
 * Cell status for a (type, side, date) in the display window.
 * Median is computed from refDates (strictly before display window).
 *
 * Returns:
 *   'all'     → all expected vendors have data at normal volume
 *   'low'     → all vendors present but at least one below threshold
 *   'partial' → some vendors absent
 *   'none'    → all vendors absent
 */
export function cellStatus(lookup, type, side, date, allDates, threshold = 80, refDates = null) {
	const byVendor = lookup?.[type]?.[side];
	if (!byVendor) return { status: 'none', present: [], missing: [], lowVolume: [] };

	// Use refDates for median if available, else fall back to allDates
	const medianDates = refDates && refDates.length > 0 ? refDates : allDates;
	const medians     = computeVendorMedians(lookup, type, side, medianDates);

	// Expected vendors = those that ever had data across ALL dates
	const expectedVendors = Object.keys(byVendor).filter(vendor =>
		allDates.some(d => (byVendor[vendor]?.[d] ?? 0) > 0)
	);
	if (!expectedVendors.length) return { status: 'none', present: [], missing: [], lowVolume: [] };

	const absent    = [];
	const lowVolume = [];
	const ok        = [];

	expectedVendors.forEach(vendor => {
		const val = byVendor[vendor]?.[date] ?? 0;
		const med = medians[vendor] ?? 0;
		if (val === 0) {
			absent.push(vendor);
		} else if (med > 0 && val < med * (threshold / 100)) {
			lowVolume.push(vendor);
		} else {
			ok.push(vendor);
		}
	});

	let status;
	if (absent.length === expectedVendors.length)  status = 'none';
	else if (absent.length > 0)                    status = 'partial';
	else if (lowVolume.length > 0)                 status = 'low';
	else                                           status = 'all';

	return { status, present: ok, missing: absent, lowVolume };
}

export function colorForStatus(status) {
	if (status === 'all')     return '#2e7d32';  // green  — all ok
	if (status === 'low')     return '#f9a825';  // yellow — low volume
	if (status === 'partial') return '#ef6c00';  // orange — some vendors missing
	return '#c62828';                            // red    — no data (worst case)
}

/**
 * vendorSideDetail — same logic as cellStatus but per vendor.
 * Uses refDates for median.
 */
export function vendorSideDetail(lookup, type, side, vendor, date, allDates, threshold = 80, refDates = null) {
	const byDate = lookup?.[type]?.[side]?.[vendor];
	if (!byDate) return null;

	const everHadData = allDates.some(d => (byDate[d] ?? 0) > 0);
	if (!everHadData) return null;

	const medianDates = refDates && refDates.length > 0 ? refDates : allDates;
	const vals        = medianDates.map(d => byDate[d] ?? 0).filter(v => v > 0);
	const med         = median(vals);

	const total   = byDate[date] ?? 0;
	const hasData = total > 0;
	const isLow   = hasData && med > 0 && total < med * (threshold / 100);

	return { vendor, side, hasData, isLow, total, median: Math.round(med) };
}

/**
 * Global metrics — uses refDates for median, displayDates for gap detection.
 */
export function computeMetrics(rows, threshold = 80, displayFrom = null, displayTo = null) {
	if (!rows.length) return {};

	const lookup       = buildLookup(rows);
	const allDates     = getUniqueDates(rows);
	const displayDates = getDisplayDates(rows, displayFrom, displayTo);
	const refDates     = getReferenceDates(rows, displayFrom);
	const types        = getUniqueTypes(rows);
	const sides        = getUniqueSides(rows);

	const gapDayDates = displayDates.filter(date =>
		types.some(type =>
			sides.some(side => {
				const { status } = cellStatus(lookup, type, side, date, allDates, threshold, refDates);
				return status !== 'all';
			})
		)
	);

	const globalPct = Math.round((displayDates.length - gapDayDates.length) / Math.max(displayDates.length, 1) * 100);

	const dayTotals = displayDates.map(date => {
		let total = 0;
		types.forEach(type => sides.forEach(side => {
			const byVendor = lookup?.[type]?.[side] ?? {};
			Object.values(byVendor).forEach(byDate => { total += byDate[date] ?? 0; });
		}));
		return { date, total };
	});
	const bestDay = [...dayTotals].sort((a, b) => b.total - a.total)[0]?.date ?? '—';

	const allVendors = [...new Set(rows.map(r => r.vendor))];
	const vendorMiss = allVendors.map(vendor => {
		let miss = 0;
		displayDates.forEach(date => {
			types.forEach(type => sides.forEach(side => {
				const detail = vendorSideDetail(lookup, type, side, vendor, date, allDates, threshold, refDates);
				if (detail && (!detail.hasData || detail.isLow)) miss++;
			}));
		});
		return { vendor, miss };
	}).sort((a, b) => b.miss - a.miss);

	return {
		globalPct,
		gapDays:         gapDayDates.length,
		gapDayDates,
		worstVendor:     vendorMiss[0]?.vendor ?? '—',
		worstVendorMiss: vendorMiss[0]?.miss   ?? 0,
		bestDay,
	};
}